import * as React from 'react';
import { gql, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import BeatLoader from 'react-spinners/BeatLoader';
import { toast } from 'react-toastify';
import { Form, Input, Label, LoginButton, Title, Welcome, Wrapper } from './styles';
import { SignInStudentMutation } from '../../../../generated/graphql';
import { useExamsTypeAndSem } from '../../../../helpers/examTypeAndSemester';
import { academicYear } from '../../../../helpers';

/**
 * StudentLoginForm
 *
 * Login form for students to access their examination timetable.
 * - Accepts an index number.
 * - On successful login, stores token and redirects to student dashboard.
 * - Shows loading spinner and error/success toasts.
 *
 * @param toggleModal - Function to close the modal after successful login.
 */
export const StudentLoginForm = ({ toggleModal }: { toggleModal: () => void }): JSX.Element => {
  // State for the student's index number input
  const [indexNumber, setIndexNumber] = React.useState<string>('');
  // For navigation after login
  const navigate = useNavigate();
  // Ref to focus the input if needed
  const indexNoInputRef = React.useRef<HTMLInputElement>(null);
  // Custom hook to get exam type and semester
  const examTypeAndSem = useExamsTypeAndSem();

  // GraphQL mutation for student sign-in
  const SIGN_IN_STUDENT = gql`
    mutation signInStudent($indexNumber: String!) {
      signInStudent(indexNumber: $indexNumber) {
        token
      }
    }
  `;

  // useMutation hook for sign-in, with generated types
  const [signInStudent, { data, loading }] = useMutation<SignInStudentMutation>(SIGN_IN_STUDENT);

  // Effect: if login is successful, store token, close modal, and redirect
  React.useEffect(() => {
    const token = data?.signInStudent?.token;
    if (data && token && !loading) {
      // save token to local storage
      localStorage.setItem('coe-student-token', token);
      toggleModal();
      navigate('/student', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /**
   * Handles login button click.
   * - Validates input.
   * - Calls sign-in mutation.
   * - Shows error toast on failure.
   */
  const handleStudentLogin = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void | null> => {
    e.preventDefault();

    if (indexNumber === '') {
      toast.warn('Please enter your index number first!');
      indexNoInputRef.current && indexNoInputRef.current.focus();
      return;
    }

    try {
      await signInStudent({ variables: { indexNumber: indexNumber.trim() } });
    } catch (err) {
      toast.error(
        err.message ||
          `There is no student with this index number. 
        If you are positive that you are providing the correct index number, 
        please contact exams support at Caesar Building (RM407/409) 
        from 9 a.m. to 5 p.m. (Monday to Friday). Thank you!`,
        {
          position: 'top-right',
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
    }
  };

  // Render the login form UI
  return (
    <>
      <Wrapper>
        <Welcome>Welcome</Welcome>
        <Title>
          {/* Exam type, semester, and academic year info */}
          {/* eslint-disable-next-line max-len*/}
          Log in to view your {examTypeAndSem && examTypeAndSem.examType.toLowerCase()}{' '}
          {examTypeAndSem?.semester} examination timetable for the {academicYear} academic year.
        </Title>
        <Form>
          <Label>Index Number</Label>
          <Input
            onChange={(e) => setIndexNumber(e.target.value)}
            value={indexNumber}
            maxLength={7}
            className="focus:bg-gray-100"
            placeholder="Enter your index number here"
            ref={indexNoInputRef}
          />
          <LoginButton style={{ color: 'white' }} onClick={handleStudentLogin}>
            {loading ? <BeatLoader color="#fff" size={20} /> : 'Log in'}
          </LoginButton>
        </Form>
      </Wrapper>
    </>
  );
};
